import { ref, readonly, computed } from 'vue';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

// ---------------------------------------------------------------------------
// Zitadel OIDC configuration (injected via Vite env vars)
// ---------------------------------------------------------------------------
const AUTHORITY = import.meta.env.VITE_ZITADEL_AUTHORITY as string | undefined;
const CLIENT_ID = import.meta.env.VITE_ZITADEL_CLIENT_ID as string | undefined;
const INTERNAL_DOMAIN = 'ororatech.com';

/** When true the entire auth layer is disabled (e.g. local dev without Zitadel). */
const authDisabled = !AUTHORITY || !CLIENT_ID;

// ---------------------------------------------------------------------------
// Singleton UserManager
// ---------------------------------------------------------------------------
let _mgr: UserManager | null = null;

function getManager(): UserManager {
  if (_mgr) return _mgr;
  if (authDisabled) throw new Error('Auth is disabled — VITE_ZITADEL_AUTHORITY / VITE_ZITADEL_CLIENT_ID not set');

  _mgr = new UserManager({
    authority: AUTHORITY!,
    client_id: CLIENT_ID!,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'openid email profile',
    loadUserInfo: true,
    automaticSilentRenew: true,
    // sessionStorage is cleared on tab close – avoids stale tokens persisting
    userStore: new WebStorageStateStore({ store: sessionStorage }),
  });

  // Propagate token events to reactive state
  _mgr.events.addUserLoaded((u) => { _user.value = u; });
  _mgr.events.addUserUnloaded(() => { _user.value = null; });
  _mgr.events.addSilentRenewError((err) => {
    console.warn('[auth] silent renew failed', err);
  });

  return _mgr;
}

// ---------------------------------------------------------------------------
// Reactive state
// ---------------------------------------------------------------------------
const _user = ref<User | null>(null);
const _initialised = ref(false);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Resolve the OIDC redirect callback. Call once from the callback route. */
async function handleCallback(): Promise<void> {
  const mgr = getManager();
  const user = await mgr.signinRedirectCallback();
  _user.value = user;
  // Remove OIDC query params from the URL
  window.history.replaceState({}, document.title, '/');
}

/**
 * Initialise the auth layer. Must be called once at app startup.
 *
 * - Checks if the current URL is an OIDC callback and processes it.
 * - Otherwise attempts to restore a session from storage.
 * - If no session exists the user is left unauthenticated (call `login()`).
 */
async function init(): Promise<void> {
  if (authDisabled) {
    _initialised.value = true;
    return;
  }

  const mgr = getManager();

  // Are we returning from Zitadel with an authorization code?
  if (window.location.pathname === '/auth/callback') {
    try {
      await handleCallback();
    } catch (err) {
      console.error('[auth] callback error', err);
    }
    _initialised.value = true;
    return;
  }

  // Try to restore an existing session
  try {
    const user = await mgr.getUser();
    if (user && !user.expired) {
      _user.value = user;
    }
  } catch {
    // No valid session — leave unauthenticated
  }

  // If still unauthenticated, redirect to Zitadel login automatically
  if (!_user.value || _user.value.expired) {
    await mgr.signinRedirect();
    return; // page will navigate away
  }

  _initialised.value = true;
}

/** Redirect to Zitadel login page. */
async function login(): Promise<void> {
  const mgr = getManager();
  await mgr.signinRedirect();
}

/** Log out from Zitadel and clear local session. */
async function logout(): Promise<void> {
  const mgr = getManager();
  await mgr.signoutRedirect();
}

/** Current access token, or null if not authenticated. */
const accessToken = computed<string | null>(() => {
  const u = _user.value;
  if (!u || u.expired) return null;
  return u.access_token;
});

/** True once `init()` has completed (regardless of auth state). */
const initialised = readonly(_initialised);

/** True when the user is authenticated with a valid (non-expired) token. */
const isAuthenticated = computed(() => !!accessToken.value);

/** User's email extracted from OIDC profile claims, or null. */
const userEmail = computed<string | null>(() => {
  const profile = _user.value?.profile;
  return (profile?.email as string | undefined)
    ?? (profile?.preferred_username as string | undefined)
    ?? null;
});

/** True when the authenticated user's email belongs to OroraTech. */
const isInternalUser = computed(() => {
  const email = userEmail.value?.trim().toLowerCase() ?? '';
  return email.endsWith(`@${INTERNAL_DOMAIN}`);
});

// ---------------------------------------------------------------------------
// Composable export
// ---------------------------------------------------------------------------
export function useAuth() {
  return {
    /** Whether the auth layer is disabled (env vars not set). */
    authDisabled,
    /** True once init() has completed. */
    initialised,
    /** True when the user has a valid access token. */
    isAuthenticated,
    /** Current access token (reactive). */
    accessToken,
    /** User email from OIDC profile. */
    userEmail,
    /** Whether the authenticated user belongs to OroraTech. */
    isInternalUser,
    /** Initialise the auth layer — call once at app startup. */
    init,
    /** Redirect to Zitadel login. */
    login,
    /** Redirect to Zitadel logout. */
    logout,
  };
}
